import { DangerousSettingWarning } from "./DangerousSettingWarning";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { Paper, Typography } from "@mui/material";
import { TractorCalibration } from "protos/atk/calibration";
import { TypedPathTo } from "portal/utils/typedPath";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";

interface SteeringValveCalibrationSubformProps<
  TBasePath extends TypedPathTo<TractorCalibration>
> {
  basePath: TBasePath;
  readOnly: boolean;
}

export const SteeringValveCalibrationSubform = <
  TBasePath extends TypedPathTo<TractorCalibration>
>({
  basePath,
  readOnly,
}: SteeringValveCalibrationSubformProps<TBasePath>): JSX.Element => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const [risksUnderstood, setRisksUnderstood] = useState<boolean>(false);

  const disabled = readOnly || !risksUnderstood;

  return (
    <Paper className="grid grid-cols-2 gap-4 p-4">
      <Typography className="col-span-full font-bold">
        {t(
          "views.autotractor.equipment.tractors.calibration.steeringValves.title"
        )}
      </Typography>
      <DangerousSettingWarning
        showConfirmationButton={!readOnly && !risksUnderstood}
        onConfirm={() => setRisksUnderstood(true)}
      />
      <FormikDimensionedNumberInput
        name={basePath.steeringCenterTrimDeg.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.steeringValves.centerTrim"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "deg",
          localSystem: measurementSystem,
          displayUnits: {
            imperial: "deg",
            metric: "deg",
          },
          disabled,
        }}
      />
      <FormikDimensionedNumberInput
        name={basePath.steeringLeftDeadbandMa.$path}
        className="col-start-1"
        label={t(
          "views.autotractor.equipment.tractors.calibration.steeringValves.leftDeadband"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "mA",
          localSystem: measurementSystem,
          minStoredValue: 0,
          displayUnits: {
            imperial: "mA",
            metric: "mA",
          },
          disabled,
        }}
      />
      <FormikDimensionedNumberInput
        name={basePath.steeringRightDeadbandMa.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.steeringValves.rightDeadband"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "mA",
          localSystem: measurementSystem,
          minStoredValue: 0,
          displayUnits: {
            imperial: "mA",
            metric: "mA",
          },
          disabled,
        }}
      />
      <FormikDimensionedNumberInput
        name={basePath.steeringControlSignalMinMa.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.steeringValves.minControlSignal"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "mA",
          localSystem: measurementSystem,
          minStoredValue: 0,
          displayUnits: {
            imperial: "mA",
            metric: "mA",
          },
          disabled,
        }}
      />
      <FormikDimensionedNumberInput
        name={basePath.steeringControlSignalMaxMa.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.steeringValves.maxControlSignal"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "mA",
          localSystem: measurementSystem,
          minStoredValue: 0,
          displayUnits: {
            imperial: "mA",
            metric: "mA",
          },
          disabled,
        }}
      />
    </Paper>
  );
};
